import React, { useEffect, useState } from "react";
import "./legal.css";

/**
 * Shared primitives for the legal pages (Terms & Conditions, Privacy Policy).
 *
 * Both mirror the same mobile app screen shape: a centered title, a
 * spinner/error/content switch, and paragraphs built from a bold lead-in plus
 * body copy. Only the section body differs between the two, so everything else
 * lives here.
 */

// ---------------------------------------------------------------------------
// Fetching
// ---------------------------------------------------------------------------

/**
 * Fetches a legal document, mirroring the app's page-scoped provider: the
 * request fires on mount, aborts on unmount, and is not cached across visits.
 *
 * @param {(signal: AbortSignal) => Promise<{success: boolean, data: any[], message?: string}>} fetcher
 *   Must be a stable reference (an `ApiService` method, not an inline arrow),
 *   since it is an effect dependency.
 * @param {string} documentName - Lowercase name used in the failure log, e.g. "privacy policy".
 */
export function useLegalDocument(fetcher, documentName) {
  const [status, setStatus] = useState("loading"); // "loading" | "error" | "success"
  const [data, setData] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;

    setStatus("loading");

    fetcher(controller.signal)
      .then((res) => {
        if (!alive) return;
        // Mirrors the app: a non-success envelope is an error, not empty data.
        if (res?.success !== true) {
          throw new Error(res?.message || `Failed to fetch ${documentName}`);
        }
        setData(res.data ?? []);
        setStatus("success");
      })
      .catch((err) => {
        // Unmounting mid-flight aborts the request — that is not a failure.
        if (!alive || err.name === "AbortError") return;
        console.error(`Failed to load ${documentName}`, err);
        setStatus("error");
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [fetcher, documentName, reloadKey]);

  return { status, data, retry: () => setReloadKey((k) => k + 1) };
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

/**
 * One point: an optional bold lead-in followed by body copy, as a single
 * continuous run so the copy wraps directly after the lead-in rather than
 * sitting in its own block.
 *
 * `header` is `null` on the Terms endpoint and `""` on the Privacy Policy one,
 * so both are normalized to a truthiness test on the trimmed value.
 */
export const PointParagraph = ({ header, point }) => {
  const hasLead = Boolean(header?.trim());

  return (
    <p className="legal-point">
      {hasLead && (
        <span className="legal-point-header">
          {header}
          {/* Only add the colon when body copy follows it. */}
          {point?.trim() ? ": " : ""}
        </span>
      )}
      {point}
    </p>
  );
};

/**
 * Page shell: title plus the three mutually exclusive body states. The title
 * stays visible in all three, matching the app's persistent app bar.
 *
 * @param {string} title - Displayed heading, e.g. "Privacy Policy".
 * @param {string} documentName - Lowercase name for the error copy, which reads
 *   "Unable to load {documentName}." verbatim from the app.
 * @param {"loading"|"error"|"success"} status
 * @param {() => void} onRetry - Web-only addition; the app has no retry affordance.
 */
export const LegalPage = ({ title, documentName, status, onRetry, children }) => (
  <div className="legal-page">
    <h1 className="legal-page-title">{title}</h1>

    {status === "loading" && (
      <div className="legal-center">
        <span className="legal-spinner" role="status" aria-label="Loading" />
      </div>
    )}

    {status === "error" && (
      <div className="legal-center">
        <p className="legal-error">
          Unable to load {documentName}.
          <br />
          Please try again later.
        </p>
        <button className="legal-retry" onClick={onRetry}>
          Try Again
        </button>
      </div>
    )}

    {status === "success" && <ul className="legal-list">{children}</ul>}
  </div>
);
